<template>
  <div v-if="items.length === 0">
    <div v-if="hasSectionSelectorApp && !isAdmin" class="flex flex-col items-center justify-center my-3 gap-4"></div>

    <div v-else class="flex flex-col items-center justify-center my-6 gap-4">
      <CIcon class="color-primary4" fa-type="fas" fixed-width icon="files" size="36" />

      <span class="color-primary3 font-size-[14px] line-height-[20px] tracking-[.25px]">
        {{ t('canvasUI.canvasBuilder.contentList.addContent') }}
      </span>

      <CButton :color="themeVars.primary" ghost :text-color="themeVars.primary" type="primary" @click="selectSections">
        {{ t('canvasUI.canvasBuilder.contentList.browseAll') }}
      </CButton>
    </div>
  </div>

  <div v-else class="p-2 flex flex-col w-full gap-4">
    <header class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:items-end">
      <div class="w-full">
        <h2 class="font-600 font-size-[15px] line-height-[28px] tracking-[0.15px] color-text">
          {{ t('canvasUI.canvasBuilder.contentList.title', { quantity: items.length }) }}
        </h2>

        <div class="font-size-[14px] line-height-[20px] tracking-[0.5px] color-primary3">
          {{ t('canvasUI.canvasBuilder.contentList.info') }}
        </div>
      </div>

      <div class="flex gap-2 w-full lg:justify-end">
        <Search
          v-model:search="search"
          class="w-full max-w-[300px] min-w-[150px]"
          :placeholder="t('canvasUI.canvasBuilder.contentList.searchPlaceholder')"
        />
      </div>
    </header>

    <div class="flex flex-col overflow-auto max-h-63vh min-h-[135px]">
      <DynamicScroller
        v-if="filteredContent.length"
        class="flex flex-col"
        :items="filteredContent"
        :key-field="filteredContent?.[0]?.component_id ? 'component_id' : 'id'"
        :min-item-size="135"
      >
        <template
          v-slot="{ item, item: { section, file, index, component_id, id, page_index }, index: loopIndex, active }"
        >
          <DynamicScrollerItem :active="active" :data-index="loopIndex" :item="item">
            <div
              class="p-4 flex gap-3 border-t-solid border-t-[1px]"
              :class="loopIndex === 0 ? 'border-transparent' : 'border-primary5'"
              :data-section-component-id="component_id"
              :data-section-index="index"
            >
              <div class="flex gap-3 items-stretch flex-shrink-1" :class="{ 'opacity-50': section?.is_draft }">
                <CThumbnail
                  :height="100"
                  :iconSize="24"
                  image-class="rounded-1 bg-primary6 justify-center items-center"
                  :item="file || section"
                  :preview-disabled="false"
                  :themeVars="themeVars"
                  :thumbnail-url="
                    file
                      ? file?.content_thumbnails[page_index ?? 0]
                      : (section.thumbnail_url ?? section.metadata?.thumbnail)
                  "
                  :width="152"
                  @click.stop
                />
              </div>

              <div class="flex flex-col gap-3 flex-auto justify-center overflow-hidden">
                <h3
                  class="font-700 font-size-[18px] tracking-[0.4px] line-height-[1.2]"
                  :class="{ 'opacity-50': section.is_draft }"
                >
                  {{ file?.name || section?.name }}

                  <template v-if="page_index && page_index > 0">
                    - {{ t('canvasUI.common.page') }} {{ page_index + 1 }}
                  </template>
                </h3>

                <div
                  class="flex gap-x-2 gap-y-1 flex-wrap overflow-auto [&:empty]:hidden"
                  :class="{ 'opacity-50': section.is_draft }"
                >
                  <CMetaDataBadge
                    v-for="(meta, key) in metadataMap.get(file?.id ?? id)"
                    :key="key"
                    :label="meta.label"
                    :value="meta.value"
                  />
                </div>

                <div class="flex flex-wrap gap-x-2 gap-y-1 items-center [&:empty]:hidden">
                  <!-- Draft Indicator -->
                  <CInfoBadge
                    v-if="section.is_draft"
                    v-tooltip="t('canvasUI.canvasBuilder.contentList.status.draftTooltip')"
                    :background-color="themeVars.text2"
                    :color="themeVars.base"
                    icon="eye-slash"
                    :text="t('canvasUI.canvasBuilder.contentList.status.draft')"
                  />

                  <!-- Popularity and Engagement -->
                  <CPopularityAndEngagementExtended
                    v-if="Object.keys(popularityDict)?.length"
                    class="gap-x-2 gap-y-1 contents!"
                    :engagement="
                      launchDarkly.show_popularity_icons
                        ? popularityDict[file?.id ?? section.id]?.engagement
                        : undefined
                    "
                    :is-recommended="recommendedSectionIds.includes(file?.id ?? section.id)"
                    :popularity="
                      launchDarkly.show_popularity_icons
                        ? popularityDict[file?.id ?? section.id]?.popularity
                        : undefined
                    "
                    :views="
                      launchDarkly.show_popularity_icons ? (popularityDict[file?.id ?? section.id]?.views ?? 0) : 0
                    "
                  />
                </div>
              </div>

              <div
                v-if="myUser && sectionComponentOverrides?.[section.id]?.modified_by?.id === myUser.id"
                class="color-primary3 font-size-[14px] font-600 line-height-[1.2] tracking-[0.4px] text-right self-center text-nowrap"
              >
                <span class="font-size-3 font-400 inline-block mb-1">{{
                  t('canvasUI.canvasBuilder.contentList.modifiedByYou')
                }}</span
                ><br />
                {{ dayjsInstance(sectionComponentOverrides?.[section.id]?.modified_at).format('MMM D, YYYY h:mm A') }}
              </div>
            </div>
          </DynamicScrollerItem>
        </template>
      </DynamicScroller>

      <div v-else class="flex flex-col items-center justify-center my-6 gap-4">
        <CIcon class="color-primary4" fa-type="fa" fixed-width icon="folder-open" :size="36" />

        <div class="flex flex-col gap-4 text-center">
          <div class="color-primary3 font-size-[14px] line-height-[20px] tracking-[.25px]">
            {{ t('canvasUI.canvasBuilder.contentList.empty') }}
          </div>

          <CButton
            :color="themeVars.primary"
            ghost
            :text-color="themeVars.primary"
            type="primary"
            @click="resetFilters"
          >
            {{ t('canvasUI.canvasBuilder.contentList.filters.reset') }}
          </CButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="tsx">
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'
import { ref, computed, type ComputedRef, inject, onMounted, type Ref } from 'vue'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types.ts'
import type { CanvasRetrieve, CanvasSection, FileRetrieve, User } from '@openapi'
import CButton from '@lib/components/CButton/CButton.vue'
import CPopularityAndEngagementExtended from '@lib/components/CPopularityAndEngagementIcons/CPopularityAndEngagementExtended.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CThumbnail from '@lib/components/CThumbnail/CThumbnail.vue'
import CInfoBadge from '@lib/components/CInfoBadge/CInfoBadge.vue'
import useThemeVars from '@lib/composables/useThemeVars.ts'
import { useI18n } from 'vue-i18n'
import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity.ts'
import useRecommendations from '@canvas-builder/composables/useRecommendations.ts'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types.ts'
import type { InstanceSettings } from '@lib/types/instanceSettings.types.ts'
import { EntityType, usePopularity } from '@canvas-builder/composables/usePopularity.ts'
import Search from '@canvas-builder/components/container/Search/Search.vue'
import CMetaDataBadge from '@lib/components/CMetaDataBadge/CMetaDataBadge.vue'
import useMetadataTemplates from '@lib/composables/useMetadataTemplates.ts'
import useCanvas from '@canvas-builder/composables/useCanvas.ts'
import dayjsInstance from 'dayjs'
import type { ContentListSection } from '@canvas-builder/types/canvas.ts'

type Item = {
  index: number
  file?: FileRetrieve
  section?: CanvasSection
} & ContentListSection

const props = defineProps<{
  name?: string
  style?: Record<string, any>
  items: Array<Item>
  hasSectionSelectorApp?: boolean
}>()

const emit = defineEmits<{
  'select-sections': []
  'open-section': [section: CanvasRetrieve['sections'][number], index: number]
}>()

const themeVars = useThemeVars()
const { t } = useI18n()
const { sectionComponentOverrides, isAdmin } = useCanvas()
const { popularityDict } = useSectionPopularity(computed(() => ({})))
const { recommendedSectionIds } = useRecommendations()
const { fetchPopularityData } = usePopularity()
const search = ref('')

const myUser = inject<Ref<User | null>>(
  'myUser',
  computed(() => null),
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const filteredContent = computed(() => {
  if (!search.value) return props.items

  return props.items.filter((item) => {
    const sectionName = item?.file?.name?.toLowerCase() || item?.section?.name?.toLowerCase() || ''
    return sectionName.includes(search.value.toLowerCase())
  })
})

const metadataMap = computed(() => {
  const skipKeys = pitcherSettings.value?.skip_metadata_keys_in_list_item || []
  const map = new Map()

  props.items.forEach(({ section, file }) => {
    const metadata = processItemMetadata(file ?? section)
    const filtered = Object.fromEntries(Object.entries(metadata).filter(([key]) => !skipKeys.includes(key)))

    if (file?.id || section?.id) {
      map.set(file?.id ?? section?.id, filtered)
    }
  })

  return map
})

const { processItemMetadata, fetchTemplate } = useMetadataTemplates()
const selectSections = () => emit('select-sections')

const resetFilters = () => {
  search.value = ''
}

onMounted(() => {
  if (launchDarkly.value.show_popularity_icons) {
    fetchPopularityData(EntityType.SECTION)
  }

  fetchTemplate()
})
</script>
